import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/site";
import { Reveal, SectionHead } from "@/components/common/Reveal";

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-12"];

const SaasMini = () => (
  <div className="mt-8 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-4" aria-hidden="true">
    {[["Tenant A", "Growth", "w-4/5"], ["Tenant B", "Starter", "w-2/5"], ["Tenant C", "Scale", "w-full"]].map(([t, p, w]) => (
      <div key={t} className="flex items-center gap-3 text-[12px]">
        <span className="w-16 font-mono text-slate-500">{t}</span>
        <span className="h-1.5 flex-1 rounded-full bg-slate-200"><span className={`grad-line block h-full rounded-full ${w}`} /></span>
        <span className="w-14 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-center text-[11px] font-semibold text-slate-700">{p}</span>
      </div>
    ))}
  </div>
);

const FlowMini = () => (
  <div className="flex flex-wrap items-center gap-2 font-mono text-[12px]" aria-hidden="true">
    {["initiated", "provider.api", "status.callback", "ledger.update", "reconciled"].map((s, i, a) => (
      <span key={s} className="flex items-center gap-2">
        <span className={`rounded border px-2.5 py-1.5 ${i === a.length - 1 ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700"}`}>{s}</span>
        {i < a.length - 1 && <span className="text-slate-400">→</span>}
      </span>
    ))}
  </div>
);

const Card = ({ s, i }) => {
  const Icon = s.icon;
  const wide = i === 5;
  return (
    <Reveal delay={(i % 3) * 0.06} className={`${SPANS[i]} bg-white`}>
      <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.key}`}
        className="group relative flex h-full flex-col p-7 transition-colors duration-300 hover:bg-slate-50 sm:p-9">
        <span className="grad-line absolute inset-x-0 top-0 h-[2px] origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100" />
        <div className={wide ? "grid gap-8 lg:grid-cols-12 lg:items-center" : "flex h-full flex-col"}>
          <div className={wide ? "lg:col-span-5" : ""}>
            <div className="flex items-start justify-between">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-slate-950 text-cyan-300 transition-transform duration-300 group-hover:-rotate-6"><Icon className="h-5 w-5" /></span>
              <span className="font-mono text-[12px] text-slate-400">0{i + 1}</span>
            </div>
            <h3 className="mt-7 text-xl font-bold tracking-tight text-slate-950 sm:text-[1.4rem]">{s.title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{s.short}</p>
          </div>
          <div className={wide ? "lg:col-span-7" : "flex flex-1 flex-col"}>
            {wide && <FlowMini />}
            <ul className={`space-y-2 text-[14px] text-slate-700 ${wide ? "mt-6 grid gap-x-6 sm:grid-cols-2 sm:space-y-0 sm:gap-y-2" : "mt-6"}`}>
              {s.bullets.map((b) => <li key={b} className="flex gap-2.5"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-[1px] bg-blue-600" />{b}</li>)}
            </ul>
            {i === 0 && <SaasMini />}
            <span className={`inline-flex items-center gap-1.5 pt-8 text-[14px] font-semibold text-slate-950 ${wide ? "" : "mt-auto"}`}>
              Explore service <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
};

export const Services = ({ index = "01" }) => (
  <section id="services" className="relative scroll-mt-20 bg-white py-24 lg:py-32" data-testid="services-section">
    <div className="container-q">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionHead index={index} label="Services" title={<>Engineering for complete<br className="hidden sm:block" /> digital products.</>} />
        <Reveal delay={0.1}><p className="max-w-sm text-[15px] leading-relaxed text-slate-600">From the first MVP to the integrations and internal tools that run behind it, six practices that cover the full product lifecycle.</p></Reveal>
      </div>
      <div className="mt-16 grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 lg:grid-cols-12">
        {SERVICES.map((s, i) => <Card key={s.slug} s={s} i={i} />)}
      </div>
    </div>
  </section>
);
